/** @jsxImportSource @emotion/react */
import { jsx } from '@emotion/react'
import { Tooltip } from '@material-ui/core'
import { trans, TranslationGroup } from 'lib/types'

const AVAILABLE_COLOR = 'rgb(95, 208, 120)'
const UNAVAILABLE_COLOR = 'rgb(175, 175, 175)'
const DIM_OPACITY = 0.2

// Build a filled crescent (annular sector) centered at (cx, cy), spanning the
// top of the circle ±52.5° from straight up — a 105° arc total.
// rInner is the inner radius, rOuter the outer.
function crescentPath(cx: number, cy: number, rInner: number, rOuter: number) {
  // 37.5° = complement of the half-span (52.5°). cos(37.5°) ≈ 0.793, sin ≈ 0.609.
  const HALF_SPAN_COMPLEMENT = (Math.PI * 37.5) / 180
  const cosA = Math.cos(HALF_SPAN_COMPLEMENT)
  const sinA = Math.sin(HALF_SPAN_COMPLEMENT)
  const sxI = cx - cosA * rInner
  const syI = cy - sinA * rInner
  const sxO = cx - cosA * rOuter
  const syO = cy - sinA * rOuter
  const exO = cx + cosA * rOuter
  const eyO = cy - sinA * rOuter
  const exI = cx + cosA * rInner
  const eyI = cy - sinA * rInner
  const f = (n: number) => n.toFixed(2)
  return (
    `M ${f(sxI)} ${f(syI)} ` +
    `L ${f(sxO)} ${f(syO)} ` +
    `A ${rOuter} ${rOuter} 0 0 1 ${f(exO)} ${f(eyO)} ` +
    `L ${f(exI)} ${f(eyI)} ` +
    `A ${rInner} ${rInner} 0 0 0 ${f(sxI)} ${f(syI)} Z`
  )
}

function WifiArcs({ bars, color }: { bars: number; color: string }) {
  // bars: 0..4
  //   0 → all dim
  //   1 → dot bright
  //   2 → dot + inner arc bright
  //   3 → dot + inner + middle arcs bright
  //   4 → everything bright
  const dotOn = bars >= 1
  const innerOn = bars >= 2
  const middleOn = bars >= 3
  const outerOn = bars >= 4

  const CX = 12
  const CY = 16

  return (
    <svg width="32" height="25" viewBox="0 0 24 18" style={{ display: 'block' }}>
      <path d={crescentPath(CX, CY, 10.3, 13)} fill={color} opacity={outerOn ? 1 : DIM_OPACITY} />
      <path d={crescentPath(CX, CY, 6.3, 9)} fill={color} opacity={middleOn ? 1 : DIM_OPACITY} />
      <path d={crescentPath(CX, CY, 2.3, 5)} fill={color} opacity={innerOn ? 1 : DIM_OPACITY} />
      <circle cx={CX} cy={CY} r={1.6} fill={color} opacity={dotOn ? 1 : DIM_OPACITY} />
    </svg>
  )
}

export function WifiSignalIcon({ rssi }: { rssi: string | number | null | undefined }) {
  const translation: TranslationGroup = trans.common()
  const value = typeof rssi === 'string' ? Number(rssi) : rssi

  const isUnavailable = value === undefined || value === null || Number.isNaN(value) || value === 0

  // WiFi RSSI thresholds (dBm) — sits 30 dB above the LTE scale
  // (LTE 4-bar threshold is -90; WiFi 4-bar threshold is -60):
  //  >= -60 → 4 bars  (excellent)
  //  >= -70 → 3 bars  (good)
  //  >= -80 → 2 bars  (fair)
  //  >= -90 → 1 bar   (poor)
  //  <  -90 → 0 bars  (unusable)
  let bars = 0
  if (!isUnavailable && typeof value === 'number') {
    if (value >= -60) bars = 4
    else if (value >= -70) bars = 3
    else if (value >= -80) bars = 2
    else if (value >= -90) bars = 1
    else bars = 0
  }

  const color = isUnavailable ? UNAVAILABLE_COLOR : AVAILABLE_COLOR

  return (
    <Tooltip enterDelay={500} placement="left" title={isUnavailable ? translation.unavailable : String(rssi)}>
      <div style={{ display: 'inline-flex', alignItems: 'flex-end', justifyContent: 'flex-end', height: '25px' }}>
        <WifiArcs bars={bars} color={color} />
      </div>
    </Tooltip>
  )
}
